import { ignore } from "./ignore.ts";
import { parked, shell } from "./native.ts";

const LINE = /\r\n|\r(?!$)|\n/gu; // a trailing lone \r waits for the next chunk (could be half of \r\n)
const DIGITS = /^\d+$/u;
const IDLE_MS = 45_000;
const RETRY_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
const JITTER = 0.5; // sleep between 50% and 100% of the backoff
/**
 * How long a page out of sight keeps its stream. Over plain HTTP (local dev: HTTP/1.1, six connections to a host for
 * all of a browser's or an app's pages) not at all. Over HTTPS (HTTP/2: one connection for all) a quick look elsewhere
 * doesn't drop it: a moment in a native app (its pages go out of sight all the time: a tab, a page pushed over), a bit
 * longer in a browser (another tab).
 */
function hiddenGrace(url: string): number {
  if (url.startsWith("http:")) {
    return 0;
  }
  return shell() ? HIDDEN_APP_MS : HIDDEN_TAB_MS;
}
const HIDDEN_APP_MS = 1000;
const HIDDEN_TAB_MS = 10_000;
const UNAUTHORIZED = 401;
const REQUEST_TIMEOUT = 408;
const TOO_MANY_REQUESTS = 429;
const CLIENT_ERROR = 400;
const SERVER_ERROR = 500;
/** Client errors that pass (a stale token: refreshed first; a timeout; too many requests): back off and retry. */
const PASSING = new Set([UNAUTHORIZED, REQUEST_TIMEOUT, TOO_MANY_REQUESTS]);
/** Any other client error won't go away by asking again; network errors and 5xx back off and retry. */
const fatal = (status: number) => status >= CLIENT_ERROR && status < SERVER_ERROR && !PASSING.has(status);

/** Event being assembled from `data:`/`event:` lines; `id` is sticky, like EventSource.lastEventId. */
interface Pending {
  data: string[];
  type: string;
  id?: string;
}

interface Connection {
  lastId?: string;
  base: number;
  attempt: number;
  status: number;
}

function applyField(ev: Pending, name: string, value: string, onRetry: (ms: number) => void): void {
  if (name === "data") {
    ev.data.push(value);
  } else if (name === "event") {
    ev.type = value;
  } else if (name === "id" && !value.includes("\0")) {
    ev.id = value;
  } else if (name === "retry" && DIGITS.test(value)) {
    onRetry(Number(value));
  }
}

function applyLine(ev: Pending, line: string, onEvent: (e: SseEvent) => void, onRetry: (ms: number) => void): void {
  if (line === "") {
    if (ev.data.length > 0) {
      onEvent({ type: ev.type || "message", data: ev.data.join("\n"), ...(ev.id !== undefined && { id: ev.id }) });
    }
    ev.data = [];
    ev.type = "";
  } else if (!line.startsWith(":")) {
    const i = line.indexOf(":");
    const raw = i < 0 ? "" : line.slice(i + 1);
    applyField(ev, i < 0 ? line : line.slice(0, i), raw.startsWith(" ") ? raw.slice(1) : raw, onRetry);
  }
}

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

/** Whether the page is out of sight (a background tab, a minimized window). Never, where there's no document. */
/** Out of sight: a hidden tab, or a page a native app keeps warm for later. */
const hidden = () => typeof document !== "undefined" && (document.visibilityState === "hidden" || parked());

/** Resolves when the page is in sight again (or `signal` aborts). */
const shown = (signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const done = () => {
      document.removeEventListener("visibilitychange", seen);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const seen = () => {
      if (!hidden()) {
        done();
      }
    };
    document.addEventListener("visibilitychange", seen);
    signal.addEventListener("abort", done, { once: true });
  });

/** Aborts `ctl` once the page has been out of sight for `ms`; returns what stops watching. */
function dropWhenHidden(ctl: AbortController, ms: number): () => void {
  if (typeof document === "undefined") {
    return ignore;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const changed = () => {
    clearTimeout(timer);
    if (hidden()) {
      timer = setTimeout(() => ctl.abort(), ms);
    }
  };
  changed();
  document.addEventListener("visibilitychange", changed);
  return () => {
    clearTimeout(timer);
    document.removeEventListener("visibilitychange", changed);
  };
}

async function pump(body: ReadableStream<Uint8Array>, kick: () => void, feed: (s: string) => void): Promise<void> {
  const reader = body.getReader();
  const utf8 = new TextDecoder();
  for (;;) {
    // biome-ignore lint/performance/noAwaitInLoops: a stream is read one chunk after another.
    const r = await reader.read();
    if (r.done) {
      return;
    }
    kick();
    feed(utf8.decode(r.value, { stream: true }));
  }
}

async function connect(url: string, o: SubscribeOpts, st: Connection): Promise<void> {
  const ctl = new AbortController();
  const abort = () => ctl.abort();
  o.signal.addEventListener("abort", abort, { once: true });
  // Out of sight a while, it lets go of its connection: a browser keeps six to a host over HTTP/1.1 (local dev), and
  // every tab's stream holding one stalls the rest. In sight again, it reconnects (onOpen: what's shown is refetched).
  const unwatch = dropWhenHidden(ctl, o.hiddenMs ?? hiddenGrace(url));
  let idle: ReturnType<typeof setTimeout> | undefined;
  const kick = () => {
    clearTimeout(idle);
    idle = setTimeout(abort, o.idleMs ?? IDLE_MS);
  };
  try {
    const tok = await o.token(st.status === UNAUTHORIZED);
    const headers = new Headers({ Accept: "text/event-stream" });
    // Signed out (a public page's stream): no token to send.
    if (tok) {
      headers.set("Authorization", `Bearer ${tok}`);
    }
    if (st.lastId !== undefined) {
      headers.set("Last-Event-ID", st.lastId);
    }
    kick();
    const res = await fetch(url, { headers, signal: ctl.signal, cache: "no-store" });
    st.status = res.status;
    if (res.ok && res.body && res.headers.get("content-type")?.startsWith("text/event-stream")) {
      st.attempt = 0;
      o.onOpen?.();
      const onEvent = (e: SseEvent) => {
        if (e.id !== undefined) {
          st.lastId = e.id;
        }
        o.onEvent(e);
      };
      const onRetry = (ms: number) => {
        st.base = ms;
      };
      await pump(res.body, kick, sseParser(onEvent, onRetry));
    } else {
      // Not a stream: its body isn't read, so the connection is let go of now.
      await res.body?.cancel();
    }
  } catch {
    // network error, abort by the watchdog, or a failed token refresh: reconnect
  } finally {
    clearTimeout(idle);
    unwatch();
    o.signal.removeEventListener("abort", abort);
  }
}

export interface SseEvent {
  type: string;
  data: string;
  id?: string;
}

interface SubscribeOpts {
  /** Access token; stale=true after a 401 -> refresh first. */
  token: (stale: boolean) => Promise<string>;
  onEvent: (e: SseEvent) => void;
  /** (Re)connected: refetch visible data to cover any gap. */
  onOpen?: () => void;
  signal: AbortSignal;
  /** The server sends heartbeats more often than this. */
  idleMs?: number;
  /** How long out of sight before it lets go of its connection (default: as hiddenGrace says). */
  hiddenMs?: number;
}

/** Push parser per the WHATWG event-stream grammar. feed() text chunks (already UTF-8 decoded, BOM stripped). */
export function sseParser(
  onEvent: (e: SseEvent) => void,
  onRetry: (ms: number) => void = ignore,
): (chunk: string) => void {
  let buf = "";
  const ev: Pending = { data: [], type: "" };
  return (chunk) => {
    buf += chunk;
    let start = 0;
    for (const m of buf.matchAll(LINE)) {
      applyLine(ev, buf.slice(start, m.index), onEvent, onRetry);
      start = m.index + m[0].length;
    }
    buf = buf.slice(start);
  };
}

/** fetch-based SSE (Authorization header), reconnect with capped exponential backoff + jitter, idle watchdog. */
export async function subscribe(url: string, o: SubscribeOpts): Promise<void> {
  const st: Connection = { base: RETRY_MS, attempt: 0, status: 0 };
  while (!o.signal.aborted) {
    if (hidden()) {
      // Out of sight: no stream until it's seen again, then straight away (no backoff: nothing failed).
      // biome-ignore lint/performance/noAwaitInLoops: waiting, then connecting, in turn.
      await shown(o.signal);
      st.attempt = 0;
      continue;
    }
    await connect(url, o, st);
    if (fatal(st.status)) {
      return;
    }
    if (hidden()) {
      continue;
    }
    const delay = Math.min(MAX_BACKOFF_MS, st.base * 2 ** st.attempt);
    st.attempt += 1;
    await sleep(delay * (JITTER + Math.random() * JITTER), o.signal);
  }
}
